import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';

// Initialize mermaid with colorful configuration
mermaid.initialize({
  startOnLoad: false,
  theme: 'base',
  securityLevel: 'loose',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  fontSize: 16,
  themeVariables: {
    // Primary colors - vibrant with dark text for visibility
    primaryColor: '#fbbf24',        // Bright Yellow
    primaryTextColor: '#1f2937',    // Dark gray text
    primaryBorderColor: '#f59e0b',
    
    // Secondary colors
    secondaryColor: '#60a5fa',      // Bright Blue
    secondaryTextColor: '#1f2937',  // Dark gray text
    secondaryBorderColor: '#3b82f6',
    
    // Tertiary colors
    tertiaryColor: '#34d399',       // Bright Green
    tertiaryTextColor: '#1f2937',   // Dark gray text
    tertiaryBorderColor: '#10b981',
    
    // Additional vibrant colors
    noteBkgColor: '#fef3c7',        // Yellow background
    noteTextColor: '#92400e',
    noteBorderColor: '#f59e0b',
    
    // Line colors
    lineColor: '#3b82f6',
    textColor: '#1f2937',           // Dark gray for all text
    mainBkg: '#fef3c7',             // Light yellow background
    
    // Node styling
    nodeBorder: '#4f46e5',
    clusterBkg: '#dbeafe',
    clusterBorder: '#3b82f6',
    
    // Git graph colors
    git0: '#6366f1',
    git1: '#ec4899',
    git2: '#10b981',
    git3: '#f59e0b',
    git4: '#8b5cf6',
    git5: '#06b6d4',
    git6: '#ef4444',
    git7: '#14b8a6',
    
    // Pie chart colors
    pie1: '#6366f1',
    pie2: '#ec4899',
    pie3: '#10b981',
    pie4: '#f59e0b',
    pie5: '#8b5cf6',
    pie6: '#06b6d4',
    pie7: '#ef4444',
    pie8: '#14b8a6',
    pie9: '#f97316',
    pie10: '#84cc16',
    pie11: '#06b6d4',
    pie12: '#a855f7',
    
    // Class diagram colors
    classText: '#1f2937',
    
    // State diagram colors
    labelColor: '#1f2937',
    
    // Sequence diagram colors
    actorBkg: '#6366f1',
    actorBorder: '#4f46e5',
    actorTextColor: '#fff',
    actorLineColor: '#6366f1',
    signalColor: '#1f2937',
    signalTextColor: '#1f2937',
    labelBoxBkgColor: '#ec4899',
    labelBoxBorderColor: '#db2777',
    labelTextColor: '#fff',
    loopTextColor: '#1f2937',
    activationBkgColor: '#dbeafe',
    activationBorderColor: '#3b82f6',
    
    // Gantt chart colors
    gridColor: '#e5e7eb',
    todayLineColor: '#ef4444',
    taskBkgColor: '#6366f1',
    taskBorderColor: '#4f46e5',
    taskTextColor: '#1f2937',
    activeTaskBkgColor: '#ec4899',
    activeTaskBorderColor: '#db2777',
    doneTaskBkgColor: '#10b981',
    doneTaskBorderColor: '#059669',
    critBkgColor: '#f59e0b',
    critBorderColor: '#d97706',
    sectionBkgColor: '#f3f4f6',
    altSectionBkgColor: '#fff',
  },
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: 'basis',
    nodeSpacing: 50,
    rankSpacing: 50,
  },
  sequence: {
    useMaxWidth: true,
    diagramMarginX: 50,
    diagramMarginY: 10,
    actorMargin: 50,
    boxMargin: 10,
    boxTextMargin: 5,
    noteMargin: 10,
    messageMargin: 35,
  },
  gantt: {
    useMaxWidth: true,
    leftPadding: 75,
    gridLineStartPadding: 35,
    fontSize: 12,
    sectionFontSize: 14,
  },
  pie: {
    useMaxWidth: true,
  },
  mindmap: {
    useMaxWidth: true,
  },
});

const MermaidDiagram = ({ 
  chart, 
  className = ''
}) => {
  const containerRef = useRef(null);
  const [svg, setSvg] = useState('');
  const [error, setError] = useState(null);
  // Use useState with lazy initialization to keep id stable across renders
  const [id] = useState(() => `mermaid-${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    if (!chart) return;

    const renderDiagram = async () => {
      try {
        // Clear previous content
        setSvg('');
        setError(null);

        // Validate and render the mermaid chart
        const { svg: renderedSvg } = await mermaid.render(id, chart);
        setSvg(renderedSvg);
      } catch (err) {
        console.error('Mermaid render error:', err);
        setError(err.message || 'Failed to render diagram');
      }
    };

    renderDiagram();
  }, [chart, id]);

  if (error) {
    return (
      <div className={`p-4 bg-red-50 border border-red-200 rounded-lg ${className}`}>
        <p className="text-red-600 font-semibold mb-2">⚠️ Diagram Syntax Error</p>
        <p className="text-sm text-red-500 mb-3">{error}</p>
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-600 hover:text-gray-900 mb-2">View diagram code</summary>
          <pre className="bg-white p-3 rounded border border-red-300 overflow-x-auto">
            <code>{chart}</code>
          </pre>
        </details>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`mermaid-container ${className}`}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

export default MermaidDiagram;
